import React from "react";
import { Link } from "gatsby";
import BigHeadline from "./BigHeadline";
import TextBlock from "./TextBlock";
import TwoColumnContent from "./TwoColumnContent";
import TabbedContent from "./TabbedContent"
import CardCollection from "./CardCollection";
import { ImageGrid, ImageCarousel } from "./ImageCollection";
import HeroCarousel from "./HeroCarousel";
import HeroDefault from "../Hero/HeroDefault";
import HeroImage from "../Hero/HeroImage";
import HeroVideo from "../Hero/HeroVideo";
import MediaSection from "./MediaSection";
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';
import { customColor } from "../../lib/helpers";
// import * as style from "./Content.module.css";

const Text = ({ children }) => {
    return (
        children[0]?.type !== "span" ?
            children.join('').length ?
                <div className="content__paragraph__container uk-container uk-container-small uk-margin-small">
                    <div className="uk-container"><p>{children}</p></div></div> : ""
            : children
    )
}
const Bold = ({ children }) => <span className="uk-text-bold">{children}</span>;
const Italic = ({ children }) => <span><em>{children}</em></span>;
const Underline = ({ children }) => <span style={{ textDecoration: "underline" }}>{children}</span>;
const List = ({ children, ...props }) => {
    const listItems = props.node.content.map(c => c.content.map(c => c.content[0].value));
    return (
        <ul className="uk-container uk-list uk-list-bullet">
            {listItems.map((item, i) =>
                <li key={`item=${i}`}>{item}</li>
            )}
        </ul>
    )
}
const BlockQuote = ({ children, ...props }) => {
    return (
        <div className="uk-container uk-flex uk-flex-center uk-flex-middle uk-padding">
            <blockquote className="uk-width-4-5 uk-width-2-5@m uk-text-yellow-dark">
                {props.node.content.map(c => c.content[0].value)}
            </blockquote>
        </div>
    )
}

const Hyperlink = ({ node, children }) => {
    return(
        <Link className="uk-text-link" to={node.data.uri}>{children}</Link>
    )
};

const Hrule = ({ node, children }) => <div className="uk-container uk-container-small"><hr className="uk-hr uk-width-3-5 uk-margin-large uk-margin-auto-left uk-margin-auto-right" /></div>;

export const layoutOptions = {

    renderMark: {
        [MARKS.BOLD]: text => <Bold>{text}</Bold>,
        [MARKS.ITALIC]: text => <Italic>{text}</Italic>,
        [MARKS.UNDERLINE]: text => <Underline>{text}</Underline>,
        [MARKS.CODE]: text => <span><div className="uk-container" dangerouslySetInnerHTML={{ __html: text }} /></span>
    },
    renderNode: {
        [BLOCKS.EMBEDDED_ASSET]: (node) => {
            const { title, description, file } = node.data.target.fields;
            const mimeType = file.contentType;
            const mimeGroup = mimeType.split('/')[0];
            switch (mimeGroup) {
                case 'image':
                    return <div className="uk-container uk-container-xsmall"><img
                        title={title ? title : null}
                        alt={description ? description : null}
                        src={file.url}
                    /></div>
                case 'video':
                    return <video controls="controls">
                        <source src="media/shuttle.mp4" type="video/mp4" />
                        Your browser does not support the HTML5 Video element.
                    </video>
                default:
                    return <span style={{ backgroundColor: 'red', color: 'white' }}> {mimeType} embedded asset</span>
            }
        },
        [BLOCKS.EMBEDDED_ENTRY]: (node) => {
            const fields = node.data.target.fields;
            switch (node.data.target.sys.contentType.sys.id) {
                case "heroCarousel":
                    return <HeroCarousel
                        props={node}
                        collectionName={fields.collectionName}
                        heroCtaModules={fields.heroCtaModules}
                        slug={fields.slug}
                    />
                case "heroCta":
                    switch (fields.templateLayout) {
                        case "Hero Image":
                            return <HeroImage data={fields} />
                        case "Fullscreen":
                            return <HeroImage data={fields} />
                        case "Hero Video":
                            return <HeroVideo data={fields} />
                        default:
                            return <HeroDefault data={fields} />
                    }
                case "bigHeadline":
                    return <BigHeadline
                        copy={fields.copy}
                        textAlign={fields.textAlign}
                        displayWidth={fields.displayWidth}
                        sectionColor={fields.sectionColor ? fields.sectionColor : null}
                        slug={fields.slug} />
                case "textBlock":
                    return <TextBlock
                        displayWidth={fields.displayWidth}
                        content={fields.content}
                        slug={fields.slug} />
                case "sectionItem":
                    return <TwoColumnContent
                        title={fields.title}
                        copy={fields.copy}
                        image={fields.image}
                        textAlign={fields.textAlign ? fields.textAlign : null}
                        displayWidth={fields.displayWidth ? fields.displayWidth : null}
                        gridLayout={fields.gridLayout ? fields.gridLayout : null}
                        sectionColor={fields.sectionColor ? fields.sectionColor : null}
                        slug={fields.slug}
                    />
                case "cardCollection":
                    return <CardCollection
                        headline={fields.headline ? fields.headline : null}
                        subHeadline={fields.subHeadline ? fields.subHeadline : null}
                        cards={fields.cards}
                        displayFormat={fields.displayFormat}
                        displayWidth={fields.displayWidth}
                        sectionColor={fields.sectionColor ? fields.sectionColor : null}
                        slug={fields.slug}
                    />
                case "tabbedContent":
                    return <TabbedContent
                        collectionName={fields.collectionName}
                        contentModules={fields.contentModules}
                        displayWidth={fields.displayWidth}
                        sectionColor={fields.sectionColor ? fields.sectionColor : null}
                        slug={fields.slug}
                    />
                case "imageCollection":
                    let backgroundColor = customColor(fields.sectionColor)
                    backgroundColor = backgroundColor !== "None" ? backgroundColor : null
                    let images = [];
                    fields.images.map(a => {
                        return images.push({
                            url: a.fields.file.url,
                            alt: a.fields.title
                        })
                    });
                    return (
                        fields.displayFormat === 'carousel' ?
                            <ImageCarousel
                                slug={fields.collectionIdentity}
                                headline={fields.headline ? fields.headline : null}
                                subHeadline={fields.subHeadline ? fields.subHeadline : null}
                                displayWidth={fields.displayWidth}
                                sectionColor={fields.sectionColor ? fields.sectionColor : null}
                                images={images}
                            />
                            :
                            <ImageGrid
                                slug={fields.collectionIdentity}
                                headline={fields.headline ? fields.headline : null}
                                subHeadline={fields.subHeadline ? fields.subHeadline : null}
                                displayWidth={fields.displayWidth}
                                sectionColor={fields.sectionColor ? fields.sectionColor : null}
                                images={images}
                            />

                    )
                case "mediaSection":
                    return <MediaSection
                        description={fields.description}
                        mediaPath={fields.mediaPath}
                        templateLayout={fields.templateLayout}
                        sectionColor={fields.sectionColor ? fields.sectionColor : null}
                    />
                default:
                    return <div></div>
            }
        },
        [BLOCKS.PARAGRAPH]: (node, children) => <Text node={node}>{children}</Text>,
        [BLOCKS.UL_LIST]: (node, children) => <div className="uk-container uk-container-small content__list"><List node={node}>{children}</List></div>,
        [BLOCKS.QUOTE]: (node, children) => <BlockQuote node={node}>{children}</BlockQuote>,
        [BLOCKS.HEADING_2]: (node, children) => <div className="uk-container uk-container-small content__heading__2"><div className="uk-container"><h2>{children}</h2></div></div>,
        [BLOCKS.HEADING_3]: (node, children) => <div className="uk-container uk-container-small content__heading__3"><div className="uk-container"><h3>{children}</h3></div></div>,
        [BLOCKS.HEADING_4]: (node, children) => <div className="uk-container uk-container-small content__heading__4"><div className="uk-container"><h4>{children}</h4></div></div>,
        [BLOCKS.HEADING_5]: (node, children) => <div className="uk-container uk-container-small content__heading__5"><div className="uk-container"><h5>{children}</h5></div></div>,
        [BLOCKS.HEADING_6]: (node, children) => <div className="uk-container uk-container-small content__heading__6"><div className="uk-container"><h6>{children}</h6></div></div>,
        [BLOCKS.HR]: (node, children) => <div className="uk-container uk-container-small"><Hrule node={node}>{children}</Hrule></div>,
        [INLINES.HYPERLINK]: (node, children) => <Hyperlink node={node}>{children}</Hyperlink>,
        [BLOCKS.TABLE]: (node, children) => {
            return (
                <section className="uk-section uk-section-small">
                    <div className="content__table__container uk-container uk-container-small">
                        <div className="uk-container">
                            <table className="uk-table uk-table-striped uk-table-middle">{children}</table>
                        </div>
                    </div>
                </section>
            )
        },
        [BLOCKS.TABLE_ROW]: (node, children) => <tr></tr>,
        [BLOCKS.TABLE_HEADER_CELL]: (node, children) => <th>{children}</th>,
        [BLOCKS.TABLE_ROW]: (node, children) => {
            console.log("TABLE ROW", children)
            if (
                children.every((node) => node.nodeType === BLOCKS.TABLE_HEADER_CELL)
            ) {
                // all children are header cells, so we should wrap the row
                // with a <thead /> tag
                return (
                    <thead>
                        <tr>{children}</tr>
                    </thead>
                );
            } else {
                // not a header row, so we can render an ordinary <tr />
                return <tr>{children}</tr>;
            }
        },
        [BLOCKS.TABLE_CELL]: (node, children) => <td>{children}</td>,
    }
}
